import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Alert, Stack } from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetNetworkQuery } from '@/core/services/network'

import PageContainer from '@/components/ui/containers/PageContainer'
import { NetworkPlanningsDashboard } from '@/features/plannings-dashboard'
import { NetworkPlanningsManagement } from '@/features/plannings-management'
import { MAIN_HEADER_HEIGHT } from '@/layouts/main'
import { PlanningsTabEnum, PlanningsTabs, usePlanningsTab } from '@/shared/plannings'

export function NetworkPlannings() {
  const { networkId } = useParams<{ networkId: string }>()
  const { t } = useTranslation()
  const tab = usePlanningsTab()

  // Fetch network data to ensure the user has access
  const { data: network, isLoading: isLoadingNetwork, error: networkError } = useGetNetworkQuery(networkId ?? skipToken)

  if (isLoadingNetwork) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    )
  }

  if (networkError || !network) {
    return (
      <PageContainer>
        <Alert severity="error">{t('page.network.plannings.error', { defaultValue: 'Error loading network data' })}</Alert>
      </PageContainer>
    )
  }

  return (
    <Stack flex={1} sx={{ maxHeight: `calc(100vh - ${MAIN_HEADER_HEIGHT}px)` }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <PlanningsTabs />
      </Box>
      <PageContainer noPadding sx={{ flex: 1, py: 2, overflow: 'auto' }}>
        {tab === PlanningsTabEnum.DASHBOARD && <NetworkPlanningsDashboard networkId={network._id} />}
        {tab === PlanningsTabEnum.ADMIN && <NetworkPlanningsManagement networkId={network._id} />}
      </PageContainer>
    </Stack>
  )
}

export default NetworkPlannings
